import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  voucherService,
  VOUCHER_AMOUNTS,
  MIN_CUSTOM_AMOUNT,
  MAX_CUSTOM_AMOUNT,
  formatCurrency,
  type PurchaseVoucherRequest,
  type PurchaseVoucherResponse,
  type VoucherTourOffer,
} from '../../services/voucherService';
import { API_BASE_URL } from '../../config/api.config';
import { useAuth } from '../../contexts/AuthContext';
import './VoucherPurchase.css';
import { begrenzeNachricht, nachrichtZaehler, MAX_NACHRICHT_ZEICHEN, MAX_NACHRICHT_ZEILEN, MAX_GESCHENKT_VON } from '../../utils/gutscheinNachricht';

interface EnabledPaymentMethods {
  stripeEnabled: boolean;
  paypalEnabled: boolean;
  klarnaEnabled: boolean;
  bankTransferEnabled: boolean;
}

interface FormData {
  amount: number;
  customAmount: string;
  buyerName: string;
  buyerEmail: string;
  buyerPhone: string;
  recipientName: string;
  recipientEmail: string;
  personalMessage: string;
  giftSenderName: string; // T-162 (v1.8.46)
  paymentMethod: 'paypal' | 'banktransfer' | 'stripe' | 'klarna';
}

const VoucherPurchase: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, user } = useAuth();

  const [formData, setFormData] = useState<FormData>({
    amount: 100,
    customAmount: '',
    buyerName: '',
    buyerEmail: '',
    buyerPhone: '',
    recipientName: '',
    recipientEmail: '',
    personalMessage: '',
    giftSenderName: '',
    paymentMethod: 'paypal',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [purchaseResult, setPurchaseResult] = useState<PurchaseVoucherResponse | null>(null);
  const [useCustomAmount, setUseCustomAmount] = useState(false);
  // Kundin-Wunsch Testphase 18.08.2026: Floßfahrt als Gutscheinwert anwählbar
  // (Wertgutschein in Höhe von Personenzahl x aktuellem Floßfahrt-Preis)
  // T-006 (02.09.2026): Fahrt wird aus den im Admin freigegebenen Tour-Typen gewählt
  // (GET /vouchers/tour-offers), Fahrtbezug wird am Gutschein gespeichert, Betrag prüft der Server.
  const [tourOffers, setTourOffers] = useState<VoucherTourOffer[]>([]);
  const [selectedOffer, setSelectedOffer] = useState<VoucherTourOffer | null>(null);
  const [useFlossfahrt, setUseFlossfahrt] = useState(false);
  const [personCount, setPersonCount] = useState(2);
  const flossfahrtPrice = selectedOffer?.pricePerPerson ?? null;
  const [enabledMethods, setEnabledMethods] = useState<EnabledPaymentMethods>({
    stripeEnabled: false,
    paypalEnabled: true,
    klarnaEnabled: false,
    bankTransferEnabled: true
  });

  // Pre-fill form with user data if logged in
  useEffect(() => {
    if (isAuthenticated && user) {
      setFormData(prev => ({
        ...prev,
        buyerName: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
        buyerEmail: user.email || '',
      }));
    }
  }, [isAuthenticated, user]);

  // Load enabled payment methods
  useEffect(() => {
    const fetchPaymentMethods = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/systemsettings/payment-methods`);
        if (response.ok) {
          const data = await response.json();
          setEnabledMethods(data);

          // Set default payment method to first available
          if (data.paypalEnabled) {
            setFormData(prev => ({ ...prev, paymentMethod: 'paypal' }));
          } else if (data.stripeEnabled) {
            setFormData(prev => ({ ...prev, paymentMethod: 'stripe' }));
          } else if (data.bankTransferEnabled) {
            // T-120 (v1.8.33): Klarna ist für Gutscheine nicht angebunden und wird hier nicht angeboten
            setFormData(prev => ({ ...prev, paymentMethod: 'banktransfer' }));
          }
        }
      } catch (err) {
        console.error('Error fetching payment methods:', err);
        // Keep defaults on error
      }
    };
    fetchPaymentMethods();
  }, []);

  // T-006: Freigegebene Fahrten laden (Admin, Tour-Typen, "Als Gutschein anbieten").
  // Der Preis ist die kleinste Staffel (Kleingruppenpreis). Ohne Freigabe bleibt der Helfer ausgeblendet.
  useEffect(() => {
    const fetchTourOffers = async () => {
      try {
        const offers = await voucherService.getTourOffers();
        if (Array.isArray(offers) && offers.length > 0) {
          setTourOffers(offers);
          setSelectedOffer(offers[0]);
        }
      } catch {
        // Helfer bleibt ausgeblendet
      }
    };
    fetchTourOffers();
  }, []);

  // Check for PayPal return
  useEffect(() => {
    const voucherId = searchParams.get('voucherId');
    const token = searchParams.get('token'); // PayPal token
    const cancelled = searchParams.get('cancelled') === 'true';

    // T-030: Bei Abbruch (cancelUrl) haengt PayPal ebenfalls token an, dann NICHT capturen
    if (voucherId && token && !cancelled) {
      handlePayPalReturn(parseInt(voucherId), token);
    }
  }, [searchParams]);

  const handlePayPalReturn = async (voucherId: number, paypalToken: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await voucherService.capturePayPalOrder(voucherId, paypalToken);
      if (result.success) {
        navigate(`/gutschein-kaufen/success?code=${result.voucherCode}&amount=${result.amount}`);
      } else if (result.status === 'PENDING') {
        // T-129 (4) (v1.8.38): Zahlung in Prüfung, kein Fehler; Hinweis statt erneutem Kauf
        setError(`${result.message || 'Ihre PayPal-Zahlung wird von PayPal noch geprüft.'} Bitte kaufen Sie keinen neuen Gutschein.`);
      } else {
        setError(result.message || 'PayPal-Zahlung fehlgeschlagen');
      }
    } catch (err: any) {
      setError(err.message || 'Fehler bei der PayPal-Zahlung');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAmountSelect = (amount: number) => {
    setFormData(prev => ({ ...prev, amount }));
    setUseCustomAmount(false);
    setUseFlossfahrt(false);
  };

  const handleCustomAmountChange = (value: string) => {
    setFormData(prev => ({ ...prev, customAmount: value }));
    setUseCustomAmount(true);
    setUseFlossfahrt(false);

    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= MIN_CUSTOM_AMOUNT && numValue <= MAX_CUSTOM_AMOUNT) {
      setFormData(prev => ({ ...prev, amount: numValue }));
    }
  };

  // Floßfahrt-Gutschein: Betrag = Personenzahl x Preis pro Person der gewählten Fahrt
  const handleFlossfahrtSelect = (persons: number, offer?: VoucherTourOffer | null) => {
    const activeOffer = offer ?? selectedOffer;
    if (!activeOffer) return;
    const computed = Math.round(persons * activeOffer.pricePerPerson * 100) / 100;
    setSelectedOffer(activeOffer);
    setPersonCount(persons);
    setUseFlossfahrt(true);
    setUseCustomAmount(false);
    setFormData(prev => ({ ...prev, amount: computed }));
  };

  const handleOfferChange = (tourTypeId: number) => {
    const offer = tourOffers.find(o => o.tourTypeId === tourTypeId) ?? null;
    if (!offer) return;
    // Personenzahl ggf. an den Höchstbetrag anpassen
    const maxPersons = Math.max(1, Math.min(20, Math.floor(MAX_CUSTOM_AMOUNT / offer.pricePerPerson)));
    handleFlossfahrtSelect(Math.min(personCount, maxPersons), offer);
  };

  // T-006: Fahrtbezug nur, wenn der Helfer aktiv gewählt ist
  const tourReference = useFlossfahrt && selectedOffer
    ? { tourTypeId: selectedOffer.tourTypeId, personCount }
    : {};

  // Generate preview PDF
  const handlePreviewPdf = async () => {
    setIsGeneratingPreview(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/vouchers/preview-pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: selectedAmount,
          buyerName: formData.buyerName || undefined,
          buyerEmail: formData.buyerEmail || undefined,
          recipientName: formData.recipientName || undefined,
          recipientEmail: formData.recipientEmail || undefined,
          personalMessage: formData.personalMessage || undefined,
          giftSenderName: formData.giftSenderName.trim() || undefined, // T-162 (v1.8.46)
          ...tourReference,
        }),
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Gutschein-Vorschau.pdf';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        setError('Fehler beim Erstellen der Vorschau');
      }
    } catch (err) {
      setError('Fehler beim Erstellen der Vorschau');
    } finally {
      setIsGeneratingPreview(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      // Validate
      if (!formData.buyerName || !formData.buyerEmail) {
        throw new Error('Bitte geben Sie Ihren Namen und E-Mail-Adresse ein');
      }

      if (formData.amount < MIN_CUSTOM_AMOUNT || formData.amount > MAX_CUSTOM_AMOUNT) {
        throw new Error(`Betrag muss zwischen ${formatCurrency(MIN_CUSTOM_AMOUNT)} und ${formatCurrency(MAX_CUSTOM_AMOUNT)} liegen`);
      }

      const request: PurchaseVoucherRequest = {
        amount: formData.amount,
        buyerName: formData.buyerName,
        buyerEmail: formData.buyerEmail,
        buyerPhone: formData.buyerPhone || undefined,
        recipientName: formData.recipientName || undefined,
        recipientEmail: formData.recipientEmail || undefined,
        personalMessage: formData.personalMessage || undefined,
        giftSenderName: formData.giftSenderName.trim() || undefined, // T-162 (v1.8.46)
        paymentMethod: formData.paymentMethod,
        ...tourReference,
      };

      const result = await voucherService.purchaseVoucher(request);
      setPurchaseResult(result);

      if (formData.paymentMethod === 'paypal') {
        await initiatePayPalPayment(result.voucherId, result.voucherCode);
      } else if (formData.paymentMethod === 'stripe') {
        // T-120 (v1.8.33): Kreditkarte über Stripe Checkout (vorher landete der Kauf auf der Vorkasse-Seite)
        await initiateStripePayment(result.voucherId, result.voucherCode);
      } else {
        // Bank transfer - show instructions with voucherId for invoice download (T-006: Fahrtbezug informativ)
        const fahrtParam = result.tourReferenceText ? `&fahrt=${encodeURIComponent(result.tourReferenceText)}` : '';
        navigate(`/gutschein-kaufen/vorkasse?code=${result.voucherCode}&amount=${result.amount}&voucherId=${result.voucherId}${fahrtParam}`);
      }
    } catch (err: any) {
      setError(err.message || 'Ein Fehler ist aufgetreten');
    } finally {
      setIsLoading(false);
    }
  };

  // T-120 (v1.8.33): Weiterleitung zu Stripe Checkout; Rückkehr auf /gutschein-kaufen/success mit session_id (Stripe setzt sie ein)
  const initiateStripePayment = async (voucherId: number, voucherCode: string) => {
    const baseUrl = window.location.origin;
    const successUrl = `${baseUrl}/gutschein-kaufen/success?voucherId=${voucherId}&provider=stripe&session_id={CHECKOUT_SESSION_ID}`;
    const cancelUrl = `${baseUrl}/gutschein-kaufen?cancelled=true&voucherId=${voucherId}`;
    const session = await voucherService.createStripeSession(voucherId, voucherCode, successUrl, cancelUrl);
    if (session.checkoutUrl) {
      window.location.href = session.checkoutUrl;
    } else {
      throw new Error('Die Kreditkartenzahlung konnte nicht gestartet werden');
    }
  };

  const initiatePayPalPayment = async (voucherId: number, voucherCode: string) => {
    const baseUrl = window.location.origin;
    // T-030 (02.09.2026): voucherId muss in Return- und Cancel-URL stehen, sonst kann die Rueckkehr von
    // PayPal (haengt nur token und PayerID an) den Kauf nicht abschliessen (VoucherSuccess/handlePayPalReturn
    // lesen voucherId aus dem Query). Backend haengt es zusaetzlich an, falls es fehlt.
    const returnUrl = `${baseUrl}/gutschein-kaufen/success?voucherId=${voucherId}`;
    const cancelUrl = `${baseUrl}/gutschein-kaufen?cancelled=true&voucherId=${voucherId}`;

    // T-026-Rest: Gutschein-Code als Besitznachweis mitgeben
    const paypalOrder = await voucherService.createPayPalOrder(voucherId, returnUrl, cancelUrl, voucherCode);

    if (paypalOrder.approvalUrl) {
      window.location.href = paypalOrder.approvalUrl;
    } else {
      throw new Error('PayPal-Bestellung konnte nicht erstellt werden');
    }
  };

  const selectedAmount = useCustomAmount
    ? parseFloat(formData.customAmount) || 0
    : formData.amount;

  return (
    <div className="voucher-purchase-page">
      <div className="voucher-purchase-container">
        <div className="voucher-header">
          <img src="/logo-frankenfloss.svg" alt="Frankenfloß Logo" className="voucher-logo" />
          <h1>Verschenke eine Floßfahrt</h1>
          <p className="voucher-subtitle">
            Mit einem Frankenfloss-Gutschein verschenken Sie unvergessliche Erlebnisse auf dem Wasser.
            3 Jahre gültig!
          </p>
        </div>

        <form onSubmit={handleSubmit} className="voucher-form">
          {error && (
            <div className="voucher-error">
              <span className="error-icon">!</span>
              {error}
            </div>
          )}

          {/* Amount Selection */}
          <section className="voucher-section">
            <h2>1. Betrag wählen</h2>

            {/* Floßfahrt als Gutscheinwert (Kundin-Wunsch Testphase 18.08.2026, T-006 mit Fahrt-Auswahl 02.09.2026) */}
            {flossfahrtPrice && selectedOffer && (
              <div
                className={`flossfahrt-helper ${useFlossfahrt ? 'selected' : ''}`}
                onClick={() => handleFlossfahrtSelect(personCount)}
              >
                <div className="flossfahrt-helper-title">Floßfahrt verschenken</div>
                {tourOffers.length > 1 ? (
                  <div className="flossfahrt-helper-row">
                    <label htmlFor="flossfahrt-tour">Fahrt:</label>
                    <select
                      id="flossfahrt-tour"
                      value={selectedOffer.tourTypeId}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => handleOfferChange(parseInt(e.target.value, 10))}
                    >
                      {tourOffers.map(o => (
                        <option key={o.tourTypeId} value={o.tourTypeId}>
                          {o.name} ({formatCurrency(o.pricePerPerson)} pro Person)
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div className="flossfahrt-helper-row">
                    <span className="flossfahrt-helper-tour">
                      {selectedOffer.name} ({formatCurrency(selectedOffer.pricePerPerson)} pro Person)
                    </span>
                  </div>
                )}
                <div className="flossfahrt-helper-row">
                  <label htmlFor="flossfahrt-persons">Anzahl Personen:</label>
                  <select
                    id="flossfahrt-persons"
                    value={personCount}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => handleFlossfahrtSelect(parseInt(e.target.value, 10))}
                  >
                    {Array.from(
                      { length: Math.max(1, Math.min(20, Math.floor(MAX_CUSTOM_AMOUNT / flossfahrtPrice))) },
                      (_, i) => i + 1
                    ).filter(n => n * flossfahrtPrice >= MIN_CUSTOM_AMOUNT) /* T-059 (v1.8.33): nur Personenzahlen ab dem Mindestbetrag */
                    .map(n => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                  <span className="flossfahrt-helper-calc">
                    {personCount} x {formatCurrency(flossfahrtPrice)} ={' '}
                    <strong>{formatCurrency(Math.round(personCount * flossfahrtPrice * 100) / 100)}</strong>
                  </span>
                </div>
                <small>
                  Wertgutschein in Höhe des aktuellen Preises der gewählten Fahrt pro Person, frei einlösbar für alle Angebote.
                  Die Fahrt wird auf dem Gutschein vermerkt.
                </small>
              </div>
            )}

            <div className="amount-buttons">
              {VOUCHER_AMOUNTS.map(amount => (
                <button
                  key={amount}
                  type="button"
                  className={`amount-btn ${formData.amount === amount && !useCustomAmount && !useFlossfahrt ? 'selected' : ''}`}
                  onClick={() => handleAmountSelect(amount)}
                >
                  {formatCurrency(amount)}
                </button>
              ))}
            </div>
            <div className="custom-amount">
              <label>Oder Wunschbetrag:</label>
              <div className="custom-amount-input">
                <input
                  type="number"
                  min={MIN_CUSTOM_AMOUNT}
                  max={MAX_CUSTOM_AMOUNT}
                  placeholder="Wunschbetrag"
                  value={formData.customAmount}
                  onChange={(e) => handleCustomAmountChange(e.target.value)}
                  className={useCustomAmount ? 'active' : ''}
                />
                <span className="currency-symbol">EUR</span>
              </div>
              <small>Min. {formatCurrency(MIN_CUSTOM_AMOUNT)} - Max. {formatCurrency(MAX_CUSTOM_AMOUNT)}</small>
            </div>
          </section>

          {/* Recipient (Optional) */}
          <section className="voucher-section">
            <h2>2. Empfänger (optional)</h2>
            <p className="section-hint">
              Wenn Sie den Gutschein verschenken möchten, können Sie hier die Daten des Beschenkten eingeben.
            </p>
            <div className="form-row">
              <div className="form-group">
                <label>Name des Beschenkten</label>
                <input
                  type="text"
                  placeholder="z.B. Max Mustermann"
                  value={formData.recipientName}
                  onChange={(e) => setFormData(prev => ({ ...prev, recipientName: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>E-Mail des Beschenkten</label>
                <input
                  type="email"
                  placeholder="max@example.com"
                  value={formData.recipientEmail}
                  onChange={(e) => setFormData(prev => ({ ...prev, recipientEmail: e.target.value }))}
                />
              </div>
            </div>
            <div className="form-group">
              <label>Persönliche Nachricht <span className="hint-inline">(max. 3 Zeilen, 250 Zeichen)</span></label>
              <textarea
                placeholder="Ihre persönliche Nachricht an den Beschenkten..."
                value={formData.personalMessage}
                onChange={(e) => {
                  // T-160 (v1.8.45): höchstens 3 Zeilen und 250 Zeichen, damit die Nachricht vollständig auf das Gutschein-PDF passt
                  const begrenzt = begrenzeNachricht(e.target.value);
                  setFormData(prev => ({ ...prev, personalMessage: begrenzt }));
                }}
                onKeyDown={(e) => {
                  const lines = formData.personalMessage?.split('\n') || [];
                  if (e.key === 'Enter' && lines.length >= MAX_NACHRICHT_ZEILEN) {
                    e.preventDefault();
                  }
                }}
                rows={3}
                maxLength={MAX_NACHRICHT_ZEICHEN}
              />
              <div className="message-counter">{nachrichtZaehler(formData.personalMessage)}, wird so auf den Gutschein gedruckt</div>
            </div>
            {/* T-162 (v1.8.46): eigene Zeile "Geschenkt von", getrennt vom Rechnungsempfänger (Ihr Name unter 3.) */}
            <div className="form-group">
              <label>Geschenkt von <span className="hint-inline">(optional, steht so auf dem Gutschein, z. B. "Deine Kollegen")</span></label>
              <input
                type="text"
                placeholder="leer = Ihr Name"
                value={formData.giftSenderName}
                maxLength={MAX_GESCHENKT_VON}
                onChange={(e) => setFormData(prev => ({ ...prev, giftSenderName: e.target.value }))}
              />
            </div>
          </section>

          {/* Buyer Info */}
          <section className="voucher-section">
            <h2>3. Ihre Daten</h2>
            <div className="form-row">
              <div className="form-group required">
                <label>Ihr Name * <span className="hint-inline">(steht als Empfänger auf der Rechnung)</span></label>
                <input
                  type="text"
                  required
                  placeholder="Ihr vollständiger Name"
                  value={formData.buyerName}
                  onChange={(e) => setFormData(prev => ({ ...prev, buyerName: e.target.value }))}
                />
              </div>
              <div className="form-group required">
                <label>Ihre E-Mail *</label>
                <input
                  type="email"
                  required
                  placeholder="ihre.email@example.com"
                  value={formData.buyerEmail}
                  onChange={(e) => setFormData(prev => ({ ...prev, buyerEmail: e.target.value }))}
                />
              </div>
            </div>
            <div className="form-group">
              <label>Telefon (optional)</label>
              <input
                type="tel"
                placeholder="+49 123 456789"
                value={formData.buyerPhone}
                onChange={(e) => setFormData(prev => ({ ...prev, buyerPhone: e.target.value }))}
              />
            </div>
          </section>

          {/* Payment Method */}
          <section className="voucher-section">
            <h2>4. Zahlungsart</h2>
            <div className="payment-methods">
              {enabledMethods.paypalEnabled && (
                <label className={`payment-option ${formData.paymentMethod === 'paypal' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="paypal"
                    checked={formData.paymentMethod === 'paypal'}
                    onChange={() => setFormData(prev => ({ ...prev, paymentMethod: 'paypal' }))}
                  />
                  <div className="payment-content">
                    <img src="/paypal-logo.svg" alt="PayPal" className="payment-logo" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                    <span>PayPal</span>
                    <small>Sofortige Aktivierung nach Zahlung</small>
                  </div>
                </label>
              )}

              {enabledMethods.stripeEnabled && (
                <label className={`payment-option ${formData.paymentMethod === 'stripe' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="stripe"
                    checked={formData.paymentMethod === 'stripe'}
                    onChange={() => setFormData(prev => ({ ...prev, paymentMethod: 'stripe' }))}
                  />
                  <div className="payment-content">
                    {/* T-120 (v1.8.33): doppelte Beschriftung "Kreditkarte Kreditkarte" entfernt */}
                    <span>Kreditkarte</span>
                    <small>Visa, Mastercard, AMEX</small>
                  </div>
                </label>
              )}

              {/* T-120 (v1.8.33): Klarna für Gutscheine nicht angebunden (Backend legte den Kauf als PayPal an), deshalb ausgeblendet */}

              {enabledMethods.bankTransferEnabled && (
                <label className={`payment-option ${formData.paymentMethod === 'banktransfer' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="banktransfer"
                    checked={formData.paymentMethod === 'banktransfer'}
                    onChange={() => setFormData(prev => ({ ...prev, paymentMethod: 'banktransfer' }))}
                  />
                  <div className="payment-content">
                    <span className="bank-icon">Bank</span>
                    <span>Vorkasse / Überweisung</span>
                    <small>Aktivierung nach Zahlungseingang (1-3 Werktage)</small>
                  </div>
                </label>
              )}
            </div>
          </section>

          {/* Summary */}
          <section className="voucher-summary">
            <h2>Zusammenfassung</h2>
            <div className="summary-content">
              <div className="summary-row">
                <span>Gutschein-Betrag:</span>
                <strong>{formatCurrency(selectedAmount)}</strong>
              </div>
              <div className="summary-row total">
                <span>Zu zahlen:</span>
                <strong>{formatCurrency(selectedAmount)}</strong>
              </div>
            </div>
          </section>

          {/* Action Buttons */}
          <div className="voucher-actions">
            <button
              type="button"
              className="voucher-preview-btn"
              onClick={handlePreviewPdf}
              disabled={isGeneratingPreview || selectedAmount < MIN_CUSTOM_AMOUNT}
            >
              {isGeneratingPreview ? 'Wird erstellt...' : 'Vorschau'}
            </button>
            <button
              type="submit"
              className="voucher-submit-btn"
              disabled={isLoading || selectedAmount < MIN_CUSTOM_AMOUNT}
            >
              {isLoading ? (
                <span className="loading-spinner">Wird bearbeitet...</span>
              ) : formData.paymentMethod === 'paypal' ? (
                'Weiter zu PayPal'
              ) : formData.paymentMethod === 'stripe' ? (
                'Weiter zur Kartenzahlung'
              ) : formData.paymentMethod === 'klarna' ? (
                'Weiter zu Klarna'
              ) : (
                'Gutschein kaufen (Vorkasse)'
              )}
            </button>
          </div>

          <p className="voucher-legal">
            Mit dem Kauf akzeptieren Sie unsere <a href="/agb" target="_blank">AGB</a> und <a href="/datenschutz" target="_blank">Datenschutzerklärung</a>.
            Gutscheine sind 3 Jahre ab Kaufdatum gültig.
          </p>
        </form>
      </div>
    </div>
  );
};

export default VoucherPurchase;
